import { IControl, Map } from 'maplibre-gl';

export type MapControlState = {
    isActive?: boolean;
    isDisabled?: boolean;
};

export abstract class AbstractMapControl implements IControl {
    protected button: HTMLButtonElement;
    protected readonly buttonContainer: HTMLDivElement = document.createElement('div');

    protected map?: Map;

    private isMounted = false;
    private isVisible = true;

    protected constructor(button?: HTMLButtonElement) {
        this.button = button ?? document.createElement('button');
    }

    public onAdd(map: Map): HTMLElement {
        this.map = map;

        this.buttonContainer.classList.add('maplibregl-ctrl');
        this.buttonContainer.classList.add('maplibregl-ctrl-group');

        this.initButton();

        if (this.isVisible) {
            this.mount();
        }

        return this.buttonContainer;
    }

    public onRemove(): void {
        if (this.buttonContainer.parentNode) {
            this.buttonContainer.parentNode.removeChild(this.buttonContainer);
        }

        this.map = undefined;
        this.isMounted = false;
    }

    protected mount(): void {
        if (this.isMounted) {
            return;
        }

        this.buttonContainer.appendChild(this.button);
        this.isMounted = true;
    }

    protected nativeButtonsStyle(container: HTMLElement, extraClasses: string[] = []): void {
        const buttons: NodeListOf<HTMLButtonElement> = container.querySelectorAll('button');

        Array.from(buttons).forEach((button: HTMLButtonElement): void => {
            button.classList.add('map-control-button');

            extraClasses.forEach((className: string): void => {
                button.classList.add(className);
            });
            button.disabled = false;
        });
    }

    protected setButtonIcon(svgHtml: string): void {
        this.button.innerHTML = svgHtml;
    }

    protected setButtonState(state: MapControlState): void {
        const isActive = state.isActive ?? false;
        const isDisabled = state.isDisabled ?? false;

        this.button.disabled = false;

        this.button.classList.toggle('is-active', isActive);
        this.button.classList.toggle('is-disabled', isDisabled);
        this.button.setAttribute('aria-pressed', String(isActive));
        this.button.setAttribute('aria-disabled', String(isDisabled));
    }

    protected setButtonTitle(title: string): void {
        this.button.removeAttribute('title');

        this.button.setAttribute('aria-label', title);

        this.buttonContainer.classList.add('map-control-tooltip');
        this.buttonContainer.dataset['tooltip'] = title;
    }

    protected setupButton(id: string, onClick: () => void, classes: string[] = []): void {
        this.button.classList.add('mapboxgl-ctrl-icon');
        this.button.classList.add('map-control-button');

        classes.forEach((className: string): void => {
            this.button.classList.add(className);
        });

        this.button.id = id;
        this.button.type = 'button';

        this.button.onclick = (): void => {
            onClick();
        };
    }

    protected setVisible(isVisible: boolean): void {
        this.isVisible = isVisible;

        if (!this.isVisible) {
            this.unmount();

            return;
        }

        this.mount();
    }

    protected unmount(): void {
        if (!this.isMounted) {
            return;
        }

        this.button.remove();
        this.isMounted = false;
    }

    protected abstract initButton(): void;
}
